import { Link } from "react-router-dom";
import { Logo } from "@/components/common/Logo";

const XIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const InstagramIcon = () => (
  <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" />
  </svg>
);

const BrandName = () => (
  <span className="font-semibold"><span className="text-slate-800 dark:text-white">Futures</span><span className="text-[#E53935]">Fury</span></span>
);

// ── Reusable footer link group ────────────────────────────────────────
const FooterLinkGroup = ({ heading, links }) => (
  <div>
    <h4 className="text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider font-semibold mb-4">
      {heading}
    </h4>
    <ul className="space-y-3 text-sm">
      {links.map((l) => (
        <li key={l.to}>
          <Link
            to={l.to}
            className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
            data-testid={l.testId}
          >
            {l.label}
          </Link>
        </li>
      ))}
    </ul>
  </div>
);

const COMPANY_LINKS = [
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact Us" },
];
const RESOURCES_LINKS = [
  { to: "/blog", label: "Blog & Guides" },
  { to: "/prop-firm-questions", label: "Prop Firm Questions", testId: "footer-ask-hub" },
  { to: "/tools", label: "Free Trading Tools" },
];
const COMPARE_LINKS = [
  { to: "/", label: "Prop Firm Rankings" },
  { to: "/community-pulse", label: "Community Pulse" },
  { to: "/how-we-rank-prop-firms", label: "How We Rank" },
];
const LEGAL_LINKS = [
  { to: "/legal", label: "Legal", testId: "footer-link-legal" },
  { to: "/disclosures", label: "Disclosures", testId: "footer-link-disclosures" },
  { to: "/terms", label: "Terms & Disclaimers", testId: "footer-link-terms" },
  { to: "/privacy-policy", label: "Privacy Policy" },
  { to: "/cookie-policy", label: "Cookie Policy" },
  { to: "/site-map", label: "Sitemap" },
];

export const Footer = () => (
  <footer className="bg-gray-50 dark:bg-slate-950 border-t border-gray-200 dark:border-white/5">
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-8 sm:gap-10">
        {/* Brand block */}
        <div className="col-span-2 md:col-span-4 lg:col-span-2">
          <Logo />
          <p className="text-slate-600 dark:text-slate-500 text-sm mt-4 max-w-md leading-relaxed">
            <BrandName /> is a futures prop firm comparison website. We partner with select prop firms and may earn commissions through affiliate links, while maintaining transparency and trader-first comparisons.
          </p>
          <div className="flex items-center gap-4 mt-5">
            <a
              href="https://x.com/futuresfury"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
              aria-label="Follow us on X (Twitter)"
              data-testid="social-x"
            >
              <XIcon />
            </a>
            <a
              href="https://instagram.com/futuresfury"
              target="_blank"
              rel="noopener noreferrer"
              className="text-slate-500 dark:text-slate-500 hover:text-slate-900 dark:hover:text-white transition-colors"
              aria-label="Follow us on Instagram"
              data-testid="social-instagram"
            >
              <InstagramIcon />
            </a>
          </div>
        </div>

        <FooterLinkGroup heading="Company" links={COMPANY_LINKS} />
        <FooterLinkGroup heading="Resources" links={RESOURCES_LINKS} />
        <FooterLinkGroup heading="Compare" links={COMPARE_LINKS} />
        <FooterLinkGroup heading="Legal" links={LEGAL_LINKS} />
      </div>
    </div>

    {/* Global Disclosures Footer Strip — visible sitewide per NinjaTrader compliance */}
    <div className="border-t border-gray-200 dark:border-amber-500/10 bg-gray-100 dark:bg-amber-500/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
        <p className="text-[11px] text-slate-500 dark:text-slate-400 leading-relaxed text-center mb-2">
          FuturesFury may earn commissions from affiliate partnerships. Rankings and reviews remain independent.
        </p>
        <p
          className="text-[11px] text-slate-600 dark:text-amber-500/80 leading-relaxed text-center"
          data-testid="footer-disclosure-strip"
        >
          <strong className="text-slate-700 dark:text-amber-400">Risk Warning:</strong> Trading futures and forex involves substantial risk and is not suitable for all investors. Past performance is not indicative of future results.{" "}
          <Link to="/disclosures" className="underline text-slate-700 dark:text-amber-400 hover:text-[#1E88E5]" data-testid="footer-read-full-disclosures">
            Read Full Disclosures
          </Link>
        </p>
      </div>
    </div>

    {/* Copyright */}
    <div className="border-t border-gray-200 dark:border-white/5">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
        <p className="text-[11px] text-slate-500 dark:text-slate-500 text-center mb-2">
          Built with secure authentication and <Link to="/how-we-rank-prop-firms#security-transparency" className="text-slate-600 dark:text-slate-400 hover:text-[#1E88E5] underline underline-offset-2 transition-colors">transparent data practices</Link>.
        </p>
        <p className="text-[11px] text-slate-500 dark:text-slate-600 text-center">
          &copy; {new Date().getFullYear()} <BrandName />. All rights reserved. Texas, USA.
          <span className="mx-2">&bull;</span>
          <Link to="/disclosures" className="text-slate-600 dark:text-slate-500 hover:text-slate-900 dark:hover:text-slate-400 transition-colors">Disclosures</Link>
        </p>
      </div>
    </div>
  </footer>
);
